import * as React from 'react';
import { createRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { rootRoute } from './__root';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { BrandFooter } from '@/components/BrandFooter';

/**
 * Each quote is a list of lines. `emphasis` controls the emotional weight:
 *   - 'punch': white, bold, larger — the payoff line
 *   - 'setup': muted (white/60), lighter weight — the build-up
 *   - undefined: white, normal — a plain beat between setup and punch
 * Line breaks come from the natural clause boundaries in the original.
 */
type QuoteLine = { text: string; emphasis?: 'punch' | 'setup' };
type Quote = readonly QuoteLine[];

const COVER_QUOTES: readonly Quote[] = [
  [
    { text: 'Slow progress', emphasis: 'setup' },
    { text: 'is still progress' },
    { text: 'forward.', emphasis: 'punch' },
  ],
  [
    { text: 'Comfort is the enemy', emphasis: 'setup' },
    { text: 'of growth.', emphasis: 'punch' },
  ],
  [
    { text: 'If you never try,', emphasis: 'setup' },
    { text: "you'll never know.", emphasis: 'punch' },
  ],
  [
    { text: 'Dream big.', emphasis: 'punch' },
    { text: 'Start small.', emphasis: 'punch' },
    { text: 'Act daily.', emphasis: 'punch' },
  ],
  [
    { text: 'Good marketing + bad product', emphasis: 'setup' },
    { text: '=  Success.' },
    { text: 'Bad marketing + good product', emphasis: 'setup' },
    { text: '=  Failure.', emphasis: 'punch' },
  ],
  [
    { text: 'Marketing is saying', emphasis: 'setup' },
    { text: 'one thing 100 times —', emphasis: 'punch' },
    { text: 'not 100 things once.', emphasis: 'setup' },
  ],
  [{ text: 'Sell the solution.', emphasis: 'punch' }],
  [
    { text: 'To be an entrepreneur,', emphasis: 'setup' },
    { text: 'you need a' },
    { text: '“fuck it, let’s just try', emphasis: 'punch' },
    { text: 'and see what happens”', emphasis: 'punch' },
    { text: 'mentality.', emphasis: 'setup' },
  ],
  [
    { text: "Ideas don't make businesses.", emphasis: 'setup' },
    { text: 'Execution does.', emphasis: 'punch' },
  ],
  [
    { text: "If you're waiting for a sign —", emphasis: 'setup' },
    { text: 'this is it.', emphasis: 'punch' },
  ],
  [
    { text: 'One day', emphasis: 'setup' },
    { text: 'or day one.' },
    { text: 'Decide.', emphasis: 'punch' },
  ],
  [
    { text: 'Everyone has ideas.', emphasis: 'setup' },
    { text: 'Few have the discipline' },
    { text: 'to turn one into reality.', emphasis: 'punch' },
  ],
  [
    { text: "You're not defined by your wins.", emphasis: 'setup' },
    { text: "You're defined by" },
    { text: 'how you handle your losses.', emphasis: 'punch' },
  ],
  [
    { text: '1 hour of doing something', emphasis: 'setup' },
    { text: 'is worth more than' },
    { text: '10 hours of thinking.', emphasis: 'punch' },
  ],
  [
    { text: 'Ask better questions.', emphasis: 'setup' },
    { text: 'Get better answers.', emphasis: 'punch' },
  ],
  [
    { text: "Don't sell products.", emphasis: 'setup' },
    { text: 'Sell perspectives.', emphasis: 'punch' },
    { text: 'Shift how you sell work.', emphasis: 'setup' },
  ],
  [
    { text: 'The smarter you become,', emphasis: 'setup' },
    { text: 'the simpler you speak.' },
    { text: "Simplicity isn't weakness —", emphasis: 'setup' },
    { text: "it's mastery.", emphasis: 'punch' },
  ],
  [
    { text: 'To become the 1%,', emphasis: 'setup' },
    { text: 'do what the other 99%' },
    { text: "won't.", emphasis: 'punch' },
  ],
  [
    { text: 'Never compete on price.', emphasis: 'setup' },
    { text: 'Compete on service' },
    { text: 'and innovation.', emphasis: 'punch' },
  ],
];

function useRotatingQuote(intervalMs: number = 5000): { quote: Quote; cycle: number } {
  const [index, setIndex] = React.useState(0);
  React.useEffect(() => {
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % COVER_QUOTES.length);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return { quote: COVER_QUOTES[index]!, cycle: index };
}

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

// Local login-form schema. Deliberately allows `totp` to be an empty string
// on the first pass (so the empty default doesn't trip the shared regex-
// scoped zod, which would silently block `handleSubmit`). The API is the
// final validator anyway.
const LoginFormSchema = z.object({
  employeeId: z.string().trim().min(1, 'Employee ID is required'),
  password: z.string().min(1, 'Password is required'),
  totp: z.string().optional(),
});
type FormValues = z.infer<typeof LoginFormSchema>;

function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const [needsTotp, setNeedsTotp] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const rotating = useRotatingQuote(5000);

  const form = useForm<FormValues>({
    resolver: zodResolver(LoginFormSchema),
    defaultValues: { employeeId: '', password: '', totp: '' },
  });

  const onSubmit = async (values: FormValues): Promise<void> => {
    setErrorMessage(null);
    setSubmitting(true);
    try {
      const payload: { employeeId: string; password: string; totp?: string } = {
        employeeId: values.employeeId.trim().toUpperCase(),
        password: values.password,
      };
      if (values.totp && values.totp.trim().length > 0) {
        payload.totp = values.totp.trim();
      }
      const res = await api.login(payload);
      if (res.status === 'ok') {
        const me = await api.me();
        if (me.mustChangePassword) {
          await navigate({ to: '/change-password' });
        } else if (res.needs2faSetup) {
          await navigate({ to: '/2fa/setup' });
        } else {
          await navigate({ to: '/' });
        }
      } else if (res.status === 'needs_totp') {
        setNeedsTotp(true);
        setErrorMessage('Enter the 6-digit code from your authenticator app.');
      } else {
        setErrorMessage('Invalid employee ID or password.');
      }
    } catch (err) {
      setErrorMessage(toMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 flex overflow-hidden bg-[hsl(var(--background))]">
      {/* Left: 60% cover — dark charcoal with rotating quote */}
      <div className="relative hidden md:block md:w-[60%]">
        <div className="absolute inset-0 bg-[linear-gradient(135deg,#050506_0%,#141416_55%,#26262c_100%)]" />
        <div
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              'radial-gradient(circle at 30% 25%, rgba(255,255,255,0.06), transparent 60%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.04), transparent 55%)',
          }}
        />
        <svg
          aria-hidden
          className="absolute inset-0 h-full w-full opacity-[0.05]"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern id="grid" width="48" height="48" patternUnits="userSpaceOnUse">
              <path d="M 48 0 L 0 0 0 48" fill="none" stroke="white" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src="/logo-mark.png" alt="" className="size-10" />
            <div className="font-display text-3xl font-semibold tracking-tight">IRIS</div>
          </div>

          <div className="flex min-h-[18rem] max-w-2xl items-center">
            <blockquote
              key={rotating.cycle}
              className="animate-login-quote flex flex-col gap-1 font-display leading-[1.15] tracking-tight"
            >
              {rotating.quote.map((line, i) => (
                <span
                  key={i}
                  className={
                    line.emphasis === 'setup'
                      ? 'text-2xl font-normal text-neutral-400 md:text-3xl'
                      : line.emphasis === 'punch'
                        ? 'text-4xl font-semibold text-white md:text-5xl lg:text-[3.5rem]'
                        : 'text-3xl font-medium text-neutral-200 md:text-4xl'
                  }
                >
                  {line.text}
                </span>
              ))}
            </blockquote>
          </div>

          <div className="text-xs uppercase tracking-widest text-neutral-500">
            Integrated Resource &amp; Information System
          </div>
        </div>
      </div>

      {/* Right: 40% form */}
      <div className="flex w-full flex-col justify-center overflow-y-auto px-6 py-8 md:w-[40%] md:px-14">
        <div className="mx-auto flex w-full max-w-sm flex-col">
          <div className="mb-8 flex items-center gap-3 md:hidden">
            <img src="/logo-mark.png" alt="" className="size-9" />
            <div className="font-display text-2xl font-semibold tracking-tight">IRIS</div>
          </div>

          <img src="/logo.png" alt="IRIS" className="mb-8 hidden h-40 w-auto self-start md:block" />

          <h1 className="font-display text-3xl font-semibold tracking-tight md:text-4xl">
            Welcome back.
          </h1>
          <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
            Sign in with your employee ID to continue.
          </p>

          {errorMessage ? (
            <Alert variant={needsTotp ? 'default' : 'destructive'} className="mt-5">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          ) : null}

          <form onSubmit={form.handleSubmit(onSubmit)} className="mt-6 flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="employeeId">Employee ID</Label>
              <Input
                id="employeeId"
                type="text"
                autoComplete="username"
                autoCapitalize="characters"
                placeholder="BITS260001"
                {...form.register('employeeId')}
              />
              {form.formState.errors.employeeId ? (
                <p className="text-xs text-[hsl(var(--destructive))]">
                  {form.formState.errors.employeeId.message}
                </p>
              ) : null}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                {...form.register('password')}
              />
              {form.formState.errors.password ? (
                <p className="text-xs text-[hsl(var(--destructive))]">
                  {form.formState.errors.password.message}
                </p>
              ) : null}
            </div>

            {needsTotp ? (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="totp">Authenticator code</Label>
                <Input
                  id="totp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="123456"
                  {...form.register('totp')}
                />
              </div>
            ) : null}

            <Button type="submit" size="lg" className="mt-2 w-full" disabled={submitting}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>

            <Link
              to="/forgot-password"
              className="text-center text-sm text-[hsl(var(--muted-foreground))] underline"
            >
              Forgot your password?
            </Link>
          </form>

          <p className="mt-8 text-xs text-[hsl(var(--muted-foreground))]">
            Admin and Finance accounts require an authenticator app on first sign-in.
          </p>

          <BrandFooter className="mt-10" />
        </div>
      </div>
    </div>
  );
}
